import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import EventTimeline from "@/components/EventTimeline";
import { CheckCircle2, XCircle, Trophy, Sparkles, RotateCcw, LayoutDashboard, Target, AlertTriangle, Layers, ShieldAlert, Eye, Wrench, HelpCircle } from "lucide-react";

function grade(pct) {
  if (pct >= 90) return { g: "A", c: "#34C759", label: "Excellent" };
  if (pct >= 75) return { g: "B", c: "#34C759", label: "Proficient" };
  if (pct >= 60) return { g: "C", c: "#FFCC00", label: "Developing" };
  if (pct >= 40) return { g: "D", c: "#FFCC00", label: "Needs Work" };
  return { g: "F", c: "#FF3B30", label: "Incomplete" };
}

export default function Scorecard() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [debrief, setDebrief] = useState(null);
  const [debriefLoading, setDebriefLoading] = useState(false);

  const loadDebrief = useCallback(async () => {
    setDebriefLoading(true);
    try {
      const r = await api.post(`/attempts/${attemptId}/debrief`);
      setDebrief(r.data.debrief);
    } catch (e) { console.error("Failed to load AI debrief", e); } finally { setDebriefLoading(false); }
  }, [attemptId]);

  useEffect(() => {
    api.get(`/attempts/${attemptId}`).then((r) => {
      setAttempt(r.data);
      if (r.data.debrief) setDebrief(r.data.debrief);
      else loadDebrief();
    });
  }, [attemptId, loadDebrief]);

  if (!attempt) return <Layout><div className="font-mono text-muted-foreground text-sm py-20 text-center animate-pulse">LOADING REPORT…</div></Layout>;

  const pct = attempt.max_score ? Math.round((attempt.score / attempt.max_score) * 100) : 0;
  const g = grade(pct);
  const completedObjs = attempt.objectives_status.filter((o) => o.completed).length;

  const replay = async () => {
    try {
      const r = await api.post("/attempts", { scenario_id: attempt.scenario_id });
      navigate(`/simulator/${r.data.attempt_id}`);
    } catch (e) { console.error("Failed to start retry", e); toast.error("Could not start retry"); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <div className="label-ot text-primary">Exercise Report</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-1">{attempt.scenario_title}</h1>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {/* Grade */}
          <div className="bg-card border border-border/60 rounded-md p-6 flex flex-col items-center justify-center" data-testid="score-grade">
            <div className="font-heading text-7xl font-extrabold" style={{ color: g.c }}>{g.g}</div>
            <div className="text-sm font-medium mt-1" style={{ color: g.c }}>{g.label}</div>
          </div>
          {/* Score */}
          <div className="bg-card border border-border/60 rounded-md p-6 md:col-span-2 flex flex-col justify-center">
            <div className="flex items-center justify-between mb-3">
              <span className="label-ot flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-status-warning" />Final Score</span>
              <span className="font-mono text-3xl font-semibold" data-testid="final-score">{attempt.score}<span className="text-base text-muted-foreground">/{attempt.max_score}</span></span>
            </div>
            <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: g.c }} />
            </div>
            <div className="flex items-center gap-6 mt-4 font-mono text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5" />{completedObjs}/{attempt.objectives_status.length} objectives</span>
              {attempt.penalty > 0 && <span className="flex items-center gap-1.5 text-status-critical"><AlertTriangle className="w-3.5 h-3.5" />-{attempt.penalty} penalty (unacked alarms)</span>}
            </div>
          </div>
        </div>

        {/* Objectives breakdown */}
        <div className="bg-card border border-border/60 rounded-md mb-6">
          <div className="px-4 py-2.5 border-b border-border/60 bg-popover"><span className="label-ot">Objective Breakdown</span></div>
          <div className="divide-y divide-border/40">
            {attempt.objectives_status.map((o) => (
              <div key={o.id} data-testid="scorecard-objective" className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {o.completed ? <CheckCircle2 className="w-5 h-5 text-status-normal shrink-0" /> : <XCircle className="w-5 h-5 text-status-critical shrink-0" />}
                  {o.kind === "decision" && <HelpCircle className="w-3.5 h-3.5 text-status-warning shrink-0" title="Analyst question" />}
                  <span className="flex-1 text-sm">{o.kind === "decision" ? o.question || o.description : o.description}</span>
                  <span className={`font-mono text-sm shrink-0 ${o.completed ? "text-status-normal" : "text-muted-foreground line-through"}`}>{o.completed ? `+${o.points}` : `0/${o.points}`}</span>
                </div>
                {o.kind === "decision" && o.explanation && (
                  <p className="text-[11px] text-muted-foreground mt-1.5 ml-8 leading-relaxed">{o.explanation}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Framework mapping */}
        {((attempt.mitre_techniques || []).length > 0 || (attempt.iec62443 || []).length > 0) && (
          <div className="bg-card border border-status-critical/30 rounded-md mb-6" data-testid="framework-mapping">
            <div className="px-4 py-2.5 border-b border-border/60 bg-popover flex items-center gap-2">
              <Layers className="w-3.5 h-3.5 text-status-critical" /><span className="label-ot text-status-critical">ATT&amp;CK for ICS &amp; IEC-62443 Mapping</span>
            </div>
            <div className="p-4 space-y-3">
              {(attempt.mitre_techniques || []).map((t) => (
                <div key={t.id} className="border border-status-critical/20 rounded-sm p-3 bg-status-critical/5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-mono text-xs font-semibold text-status-critical">{t.id}</span>
                    <span className="text-sm font-medium">{t.name}</span>
                    <span className="font-mono text-[10px] text-muted-foreground ml-auto">{t.tactic}</span>
                  </div>
                  <div className="flex gap-2 text-[11px] mb-1"><Eye className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /><span><span className="text-primary font-semibold">Detection:</span> <span className="text-foreground/80">{t.detection}</span></span></div>
                  <div className="flex gap-2 text-[11px]"><Wrench className="w-3.5 h-3.5 text-status-normal shrink-0 mt-0.5" /><span><span className="text-status-normal font-semibold">Mitigation:</span> <span className="text-foreground/80">{t.mitigation}</span></span></div>
                </div>
              ))}
              {(attempt.iec62443 || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {attempt.iec62443.map((f) => (
                    <div key={f.fr} className="border border-primary/30 bg-primary/5 rounded-sm px-2.5 py-1.5">
                      <div className="flex items-center gap-2">
                        <ShieldAlert className="w-3.5 h-3.5 text-primary" />
                        <span className="font-mono text-[11px] font-semibold text-primary">{f.fr}</span>
                        <span className="text-xs">{f.name}</span>
                        {f.sl != null && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-primary/15 text-primary">SL{f.sl}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI Debrief */}
        <div className="bg-card border border-primary/30 rounded-md mb-6" data-testid="ai-debrief">
          <div className="px-4 py-2.5 border-b border-border/60 bg-popover flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-primary" /><span className="label-ot text-primary">AI Instructor Debrief</span>
          </div>
          <div className="p-4 text-sm leading-relaxed text-foreground/90">
            {debriefLoading ? <span className="font-mono text-muted-foreground animate-pulse">Generating debrief…</span> : (debrief || "No debrief available.")}
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-6"><EventTimeline events={attempt.events} /></div>

        <div className="flex flex-wrap gap-3">
          <Button data-testid="replay-button" onClick={replay} className="rounded-sm font-semibold"><RotateCcw className="w-4 h-4 mr-2" />Retry Scenario</Button>
          <Button data-testid="dashboard-button" onClick={() => navigate("/dashboard")} variant="outline" className="rounded-sm font-semibold border-border"><LayoutDashboard className="w-4 h-4 mr-2" />Back to Dashboard</Button>
        </div>
      </div>
    </Layout>
  );
}
